import { useNavigate } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Newsletter from "@/components/Newsletter";
import { FadeIn, ChapterLabel } from "@/components/Reveal";
import { BOOKS, YOUTUBE_URL } from "@/data/content";
import useSEO from "@/hooks/useSEO";

export default function Stories() {
  useSEO(
    "Stories & Picture Books for Ages 4-8 | The Cap Chronicles",
    "Explore The Cap Chronicles bookshelf, with illustrated and rhyming picture books with read-aloud videos for kids ages 4-8, about courage, kindness, and self-belief."
  );
  const navigate = useNavigate();
  const featured = BOOKS.find((b) => b.featured);
  const rest = BOOKS.filter((b) => !b.featured);

  return (
    <main>
      <PageHero
        kicker="The Stories"
        title="Every cap has"
        italic="a story."
        description="The Cap Chronicles is a world of illustrated and rhyming picture books with YouTube read-alouds for kids ages 4-8, full of gentle adventures about courage, imagination, and the magic tucked inside ordinary things. We won't spoil the endings. Start anywhere. Carry them everywhere."
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12" data-testid="featured-book">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <FadeIn className="relative order-2 mx-auto w-full max-w-md lg:order-1">
            <div className="absolute left-1/2 top-1/2 h-[110%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-sky/20 blur-3xl" />
            <img src={featured.cover} alt={`${featured.title} cover art`} className="relative w-full rotate-[2deg] rounded-3xl object-cover shadow-[0_40px_80px_-30px_rgba(34,34,34,0.4)]" />
          </FadeIn>
          <div className="order-1 lg:order-2">
            <ChapterLabel num="01" title="The First Story" />
            <FadeIn delay={0.1}>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky">{featured.chapter}</p>
              <h2 className="mt-3 font-serif text-4xl leading-tight tracking-tight md:text-5xl">{featured.title}</h2>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-charcoal/70 md:text-lg">{featured.description}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {featured.themes.map((t) => (
                  <span key={t} className="rounded-full bg-forest/10 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-forest">{t}</span>
                ))}
              </div>
              <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" data-testid="stories-watch-featured-button" className="mt-10 inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-[1.03]">
                <Play size={14} fill="currentColor" /> Watch on YouTube
              </a>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-32" data-testid="book-index">
        <ChapterLabel num="02" title="The Bookshelf" />
        <div className="flex flex-col">
          {rest.map((book, i) => (
            <FadeIn key={book.id} delay={i * 0.08}>
              <article onClick={() => navigate(`/stories/${book.id}`)} className="group grid cursor-pointer items-center gap-6 border-t border-charcoal/10 py-12 md:grid-cols-[auto_auto_1fr_auto] md:gap-12" data-testid={`book-row-${book.id}`}>
                <span className="font-serif text-6xl italic text-charcoal/15 md:text-7xl">{String(i + 2).padStart(2, "0")}</span>
                <img src={book.cover} alt={`${book.title} cover art`} className="h-36 w-28 rounded-2xl object-cover shadow-lg transition-transform duration-500 group-hover:scale-[1.04] group-hover:rotate-[-1.5deg]" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky">{book.chapter}</p>
                  <h2 className="mt-2 font-serif text-3xl tracking-tight transition-colors duration-300 group-hover:text-forest md:text-4xl">{book.title}</h2>
                  <p className="mt-4 max-w-2xl text-base leading-relaxed text-charcoal/70">{book.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {book.themes.map((t) => (
                      <span key={t} className="rounded-full bg-gold/15 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-charcoal/70">{t}</span>
                    ))}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-forest">
                    Open the story page <ArrowRight size={13} />
                  </span>
                </div>
                <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`book-watch-${book.id}`} className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-charcoal/20 text-charcoal transition-all duration-300 hover:border-forest hover:bg-forest hover:text-cream md:h-16 md:w-16" aria-label={`Watch ${book.title}`}>
                  <Play size={18} fill="currentColor" />
                </a>
              </article>
            </FadeIn>
          ))}
          <FadeIn>
            <div className="flex flex-col items-start justify-between gap-4 border-t border-charcoal/10 py-12 sm:flex-row sm:items-center">
              <p className="font-serif text-2xl italic text-charcoal/50">New stories arrive with the seasons…</p>
              <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" data-testid="stories-subscribe-button" className="group inline-flex items-center gap-2 rounded-full bg-sky px-7 py-3.5 text-sm font-semibold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-[1.03]">
                Subscribe on YouTube <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      <Newsletter />
    </main>
  );
}
